import {
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	useTheme,
} from "@mui/material";
import {
	MoreVerticalIcon,
	PencilIcon,
	RefreshCwIcon,
	Trash2Icon,
} from "lucide-react";
import { type MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDeletePortfolioMutation } from "web_myplann/Api/Portfolio/Mutations/useDeletePortfolioMutation";
import { useDeletePropertyMutation } from "web_myplann/Api/Portfolio/Mutations/useDeletePropertyMutation";
import {
	useIsViewer,
	usePortfolioRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import type {
	ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { isServiceOrAdvisoryDomain } from "web_myplann/Portfolio/Helpers/isServiceOrAdvisoryDomain";
import { portfolioDomainRegistry } from "web_myplann/Portfolio/Helpers/portfolioDomainRegistry";
import { propertyTypeRegistry } from "web_myplann/Portfolio/Helpers/propertyTypeRegistry";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { DeleteItemDrawer } from "web_myplann/Shared/Components/DeleteItemDrawer";

type PortfolioDetailMenuProps = (
	| {
			kind: PortfolioItemKind.Property;
			id: number;
			propertyType: ClientPropertyTypeEnum;
			title: string;
	  }
	| {
			kind: PortfolioItemKind.Portfolio;
			id: number;
			domain: ClientPortfolioDomainEnum;
			title: string;
	  }
) & {
	onUpdateValue?: () => void;
};

const resolveEditPath = (
	props: PortfolioDetailMenuProps,
): string | undefined =>
	props.kind === PortfolioItemKind.Property
		? propertyTypeRegistry[props.propertyType]?.editRoute(props.id)
		: portfolioDomainRegistry[props.domain]?.editRoute(props.id);

const resolveEditCopy = (props: PortfolioDetailMenuProps) => {
	if (props.kind === PortfolioItemKind.Property) {
		return Resources.Portfolio.Actions.editProperty;
	}

	return isServiceOrAdvisoryDomain(props.domain)
		? Resources.Portfolio.Actions.editService
		: Resources.Portfolio.Actions.editContract;
};

export const PortfolioDetailMenu = (props: PortfolioDetailMenuProps) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const open = anchorEl !== null;

	const { mutate: deleteProperty, isPending: isDeletingProperty } =
		useDeletePropertyMutation();
	const { mutate: deletePortfolio, isPending: isDeletingPortfolio } =
		useDeletePortfolioMutation();

	const isPending = isDeletingProperty || isDeletingPortfolio;

	const { hasRight: hasPortfolioRight } = usePortfolioRightOptional();
	const isViewer = useIsViewer();

	const editPath = resolveEditPath(props);

	const deleteCopy =
		props.kind === PortfolioItemKind.Property
			? Resources.Portfolio.Actions.DeleteProperty
			: Resources.Portfolio.Actions.DeletePortfolio;

	const itemTitle = props.title;

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleEdit = () => {
		handleClose();
		if (editPath) {
			navigate(editPath);
		}
	};

	const handleUpdateValue = () => {
		handleClose();
		props.onUpdateValue?.();
	};

	const handleDelete = () => {
		handleClose();
		setDeleteOpen(true);
	};

	const handleDeleteSuccess = () => {
		setDeleteOpen(false);
		toast.success(t(deleteCopy.successToast));
		navigate(Routing.Portfolio.PortfolioList.path());
	};

	const handleConfirmDelete = () => {
		if (props.kind === PortfolioItemKind.Property) {
			deleteProperty(props.id, { onSuccess: handleDeleteSuccess });
		} else {
			deletePortfolio(props.id, { onSuccess: handleDeleteSuccess });
		}
	};

	if (!hasPortfolioRight || isViewer) {
		return null;
	}

	return (
		<>
			<IconButton onClick={handleOpen} size="small">
				<MoreVerticalIcon
					size={theme.iconSize.md}
					color={theme.palette.grey[600]}
				/>
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				redesign
			>
				{props.onUpdateValue && (
					<MenuItem key="updateValue" onClick={handleUpdateValue}>
						<ListItemIcon>
							<RefreshCwIcon size={theme.iconSize.sm} />
						</ListItemIcon>
						<ListItemText>
							{t(Resources.Portfolio.Actions.updateValue)}
						</ListItemText>
					</MenuItem>
				)}
				{editPath && (
					<MenuItem key="edit" onClick={handleEdit}>
						<ListItemIcon>
							<PencilIcon size={theme.iconSize.sm} />
						</ListItemIcon>
						<ListItemText>{t(resolveEditCopy(props))}</ListItemText>
					</MenuItem>
				)}
				<Divider key="divider" />
				<MenuItem key="delete" onClick={handleDelete}>
					<ListItemIcon>
						<Trash2Icon
							size={theme.iconSize.sm}
							color={theme.palette.error.main}
						/>
					</ListItemIcon>
					<ListItemText
						slotProps={{ primary: { color: theme.palette.error.main } }}
					>
						{t(Resources.Portfolio.Actions.deleteContract)}
					</ListItemText>
				</MenuItem>
			</Menu>
			<DeleteItemDrawer
				open={deleteOpen}
				itemTitle={itemTitle}
				namespace={deleteCopy}
				isPending={isPending}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};
